import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { getData, postData, putData } from '../backend';
import { CompanySize, UserProfile } from '../interfaces';
import { COMPANY_INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS, WHY_PORTANT_OPTIONS } from '../utils';
import { useModal } from '../contexts';
import useUser from '../hooks/useUser';
import AppLoading from './AppLoading';
import AppPortal from './AppPortal';
import ErrorModal from './ErrorModal';
import {
  ONBOARDING_SAMPLE_COMPANY,
  ONBOARDING_STEPS,
  ONBOARDING_TEMPLATES,
  OnboardingDataSelection,
  OnboardingStep,
  OnboardingTemplate,
} from './onboarding/constants';
import OnboardingLayout, { OVERLAY_INTRO_MS } from './onboarding/OnboardingLayout';
import OnboardingDriveConnect from './onboarding/OnboardingDriveConnect';
import OnboardingData from './onboarding/OnboardingData';
import OnboardingQuestions, { BLANK_ANSWERS, OnboardingAnswers } from './onboarding/OnboardingQuestions';
import OnboardingPdfFrame from './onboarding/OnboardingPdfFrame';
import useOnboardingGeneration from './onboarding/useOnboardingGeneration';
import OnboardingConfirmRegenerateModal from './onboarding/OnboardingConfirmRegenerateModal';
import OnboardingConfirmSkipModal from './onboarding/OnboardingConfirmSkipModal';
import OnboardingGenerateToast from './onboarding/OnboardingGenerateToast';
import OnboardingTemplateGraphic from './onboarding/OnboardingTemplateGraphic';

function Onboarding() {
  const history = useHistory();
  const { t } = useTranslation();
  const { openModal } = useModal();

  const { user, refreshUser, markOnboardingComplete } = useUser();

  // Same key as useWorkflows() so the workflow list is already cached afterwards
  const { data: workflows, error: workflowsError } = useSWR<{ total: number }>(user ? '/workflows/?page=1&type=ALL' : null, getData);

  // Decided once, the onboarding document itself creates a workflow and would otherwise flip this mid-flow
  const [hasWorkflows, setHasWorkflows] = useState<boolean | null>(null);

  useEffect(() => {
    if (hasWorkflows === null && (workflows || workflowsError))
      setHasWorkflows(!!workflows?.total);
  }, [hasWorkflows, workflows, workflowsError]);

  const generation = useOnboardingGeneration();

  const [step, setStep] = useState<OnboardingStep>(ONBOARDING_STEPS.TEMPLATE);
  const [template, setTemplate] = useState<OnboardingTemplate | null>(null);
  const [driveReady, setDriveReady] = useState(false);
  const [dataSelection, setDataSelection] = useState<OnboardingDataSelection | null>(null);
  const [showTaggedGraphic, setShowTaggedGraphic] = useState(false);
  const [answers, setAnswers] = useState<OnboardingAnswers>(BLANK_ANSWERS);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toastHidden, setToastHidden] = useState(false);
  const [inviteDismissed, setInviteDismissed] = useState(false);
  const [inviteAccepting, setInviteAccepting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const invite = user?.pendingInvites?.[0];
  const hasDataSelection = !!dataSelection;

  useEffect(() => {
    if (!hasDataSelection) {
      setShowTaggedGraphic(false);
      return;
    }
    const timer = setTimeout(() => setShowTaggedGraphic(true), OVERLAY_INTRO_MS);
    return () => clearTimeout(timer);
  }, [hasDataSelection]);
  const profileWrite = useRef(Promise.resolve());

  const profile = user?.profile;
  const writeProfile = useCallback((update: Partial<UserProfile>) => {
    if (!profile)
      return Promise.resolve();

    const next = { ...profile, ...update };
    const request = profileWrite.current
      .catch(() => undefined)
      .then(() => putData("/auth/profile/", next));
    profileWrite.current = request.then(() => undefined, () => undefined);
    return request;
  }, [profile]);

  // Only the stage is stored on the profile, so resuming prefills the answers rather than jumping to a step
  const prefilled = useRef(false);
  useEffect(() => {
    if (!profile || prefilled.current)
      return;
    prefilled.current = true;

    const option = (options: { value: string }[], value?: string) => options.some(opt => opt.value === value) ? value! : "";
    setAnswers({
      companyName: profile.companyName ?? "",
      companySize: option(COMPANY_SIZE_OPTIONS, profile.companySize) as CompanySize,
      companyIndustry: option(COMPANY_INDUSTRY_OPTIONS, profile.companyIndustry),
      heardAboutFrom: option(WHY_PORTANT_OPTIONS, profile.heardAboutFrom),
    });
  }, [profile]);

  const questionsComplete = !!answers.companyName.trim() && !!answers.companySize && !!answers.companyIndustry && !!answers.heardAboutFrom;
  const questionsAnswered = (profile?.onboardingCurrentStage ?? 0) >= ONBOARDING_STEPS.REVEAL && questionsComplete;

  useEffect(() => {
    if (step === ONBOARDING_STEPS.QUESTIONS && questionsAnswered && (generation.status === 'success' || generation.status === 'skipped'))
      setStep(ONBOARDING_STEPS.REVEAL);
  }, [step, questionsAnswered, generation.status]);

  // Give the document a chance to generate before offering to skip
  const onGeneratingPage = step === ONBOARDING_STEPS.QUESTIONS && questionsAnswered;
  const [generatingSkipShown, setGeneratingSkipShown] = useState(false);
  useEffect(() => {
    if (!onGeneratingPage)
      return;
    setGeneratingSkipShown(false);
    const timer = setTimeout(() => setGeneratingSkipShown(true), 60_000);
    return () => clearTimeout(timer);
  }, [onGeneratingPage]);

  useEffect(() => {
    if (!profile || (invite && !inviteDismissed))
      return;
    if (step >= ONBOARDING_STEPS.REVEAL)
      return;
    if (profile.onboardingCurrentStage >= step)
      return;

    writeProfile({ onboardingCurrentStage: step })
      .then(() => refreshUser())
      .catch(console.error);
  }, [step, profile, invite, inviteDismissed, refreshUser, writeProfile]);

  async function handleAcceptInvite() {
    if (!invite)
      return;

    setInviteAccepting(true);
    setInviteError(null);
    try {
      await postData('/auth/team/accept-invite/', { team: invite.team.id });
      await markOnboardingComplete().catch(console.error);
      window.location.href = '/workflows';
    } catch (e: any) {
      setInviteAccepting(false);
      setInviteError(e?.message || t('Failed to accept invite'));
    }
  }

  function handleStartGeneration(selected: OnboardingTemplate, data: OnboardingDataSelection) {
    setToastHidden(false);
    setStep(ONBOARDING_STEPS.QUESTIONS);
    generation.start(selected, data);
  }

  async function handleSaveQuestions() {
    setSaving(true);
    setSaveError(null);
    try {
      if (user?.profile) {
        const company = {
          companyName: answers.companyName.trim(),
          companySize: answers.companySize,
          companyIndustry: answers.companyIndustry,
          onboardingCurrentStage: ONBOARDING_STEPS.REVEAL,
        };
        try {
          await writeProfile({ ...company, heardAboutFrom: answers.heardAboutFrom });
        } catch (e) {
          console.error(e);
          await writeProfile(company);
        }
      }

      // TODO: Automatically create first team after onboarding P2 (Teams) changes
      // if (!user?.activeTeam)
      //   await postData('/auth/team/', { name: answers.companyName.trim() });

      await refreshUser();
      setStep(ONBOARDING_STEPS.REVEAL);
    } catch (e: any) {
      console.error(e);
      const detail = typeof e?.payload?.detail === "string" ? e.payload.detail : null;
      const message = typeof e?.message === "string" && !e.message.startsWith("{") ? e.message : null;
      setSaveError(detail || message || t("Sorry we couldn't store your details at this time. You can continue and update them later in Settings."));
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete() {
    try {
      await markOnboardingComplete();
      history.replace(generation.workflowId ? `/w/${generation.workflowId}` : '/workflows');
    } catch (e) {
      console.error(e);
      openModal(<ErrorModal details={e} />);
    }
  }

  function completeWithAnswers(stage: OnboardingStep) {
    // Keep whatever questions were answered, blank ones would overwrite existing profile values
    const answered: Partial<UserProfile> = Object.fromEntries(
      Object.entries({ ...answers, companyName: answers.companyName.trim() }).filter(([, value]) => value)
    );
    return markOnboardingComplete(stage, answered).catch((e) => {
      if (!answered.heardAboutFrom)
        throw e;
      console.error(e);
      const { heardAboutFrom, ...rest } = answered;
      return markOnboardingComplete(stage, rest);
    });
  }

  async function handleSkip() {
    try {
      await completeWithAnswers(step);
      history.replace('/workflows');
    } catch (e) {
      console.error(e);
      openModal(<ErrorModal details={e} />);
    }
  }

  const onSkip = () => openModal(<OnboardingConfirmSkipModal onConfirm={handleSkip} />);

  async function handleSaveProfile() {
    setSaving(true);
    setSaveError(null);
    try {
      await completeWithAnswers(ONBOARDING_STEPS.REVEAL);
      history.replace('/workflows');
    } catch (e: any) {
      console.error(e);
      setSaveError(t("Sorry we couldn't store your details at this time. You can continue and update them later in Settings."));
      setSaving(false);
    }
  }

  if (!user)
    return <AppPortal />;

  if (invite && !inviteDismissed) {
    return (
      <OnboardingLayout
        title={t('You have been invited to join a team!')}
        continueLabel={inviteAccepting ? t('Joining...') : t('Accept invite')}
        continueDisabled={inviteAccepting}
        onContinue={handleAcceptInvite}
        onSkip={onSkip}
        notice={inviteError}
      >
        <div className="flex flex-col gap-4">
          <p className="font-semibold text-gray-400">
            {t('Join')} <span className="text-gray-600">{invite.team.name}</span> {t('to start collaborating.')}
          </p>
          <button type="button" className="font-gilroy font-semibold text-sm text-gray-400 hover:text-gray-600 text-left" onClick={() => setInviteDismissed(true)}>
            {t('Set up my own account')}
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  if (hasWorkflows === null)
    return <AppLoading />;

  // Existing users only need to fill in their profile, not generate a first document
  if (hasWorkflows) {
    return (
      <OnboardingLayout
        title={t('Complete your profile')}
        subtitle={t('This will help us find ways to save you the most time, improve the product experience, and help you get the most out of Portant.')}
        notice={saveError}
        continueDisabled={!questionsComplete || saving}
        continueLabel={saving ? t('Saving...') : t('Continue')}
        onContinue={handleSaveProfile}
        onSkip={onSkip}
      >
        <OnboardingQuestions answers={answers} onChange={setAnswers} />
      </OnboardingLayout>
    );
  }

  if (!template || step === ONBOARDING_STEPS.TEMPLATE) {
    return (
      <OnboardingLayout
        title={t("Let's make a test document to show you how it works")}
        subtitle={t('Pick a starter template. You can customize it later.')}
        step={ONBOARDING_STEPS.TEMPLATE}
        preview={template && <OnboardingTemplateGraphic src={template.graphic} alt={template.label} />}
        continueDisabled={!template || !driveReady}
        onContinue={() => setStep(ONBOARDING_STEPS.DATA)}
        onSkip={onSkip}
      >
        <div className="flex gap-2 p-0.5">
          {ONBOARDING_TEMPLATES.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`relative flex-1 h-20 px-6 rounded-lg border border-gray-200 bg-white text-left font-gilroy font-semibold text-base text-black learn-card-hover ${template?.key === option.key ? 'learn-card-selected' : ''}`}
              onClick={() => setTemplate(option)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
        {template && <OnboardingDriveConnect onReadyChange={setDriveReady} />}
      </OnboardingLayout>
    );
  }

  function handleContinueFromData() {
    if (!template || !dataSelection)
      return;
    const selectedTemplate = template;
    const selectedData = dataSelection;
    if (generation.status === 'idle')
      handleStartGeneration(selectedTemplate, selectedData);
    else if (generation.isFor(selectedTemplate, selectedData))
      setStep(ONBOARDING_STEPS.QUESTIONS);
    else
      openModal(<OnboardingConfirmRegenerateModal onConfirm={() => handleStartGeneration(selectedTemplate, selectedData)} />);
  }

  if (step === ONBOARDING_STEPS.DATA || !dataSelection) {
    return (
      <OnboardingLayout
        title={t('Now select a test deal or use our sample data')}
        subtitle={t('Pick a deal to fill your first document, or use sample data.')}
        step={ONBOARDING_STEPS.DATA}
        preview={<OnboardingTemplateGraphic src={showTaggedGraphic ? template.taggedGraphic : template.graphic} alt={template.label} />}
        previewOverlay={dataSelection && (
          <img
            src={dataSelection.kind === "hubspot_deal" ? "/assets/onboarding/data-modal-hubspot.svg" : "/assets/onboarding/data-modal-sample.svg"}
            alt=""
            className="block w-full select-none"
          />
        )}
        overlayKey={dataSelection?.kind ?? ""}
        onBack={() => {
          setDataSelection(null);
          setStep(ONBOARDING_STEPS.TEMPLATE);
        }}
        continueDisabled={!dataSelection}
        onContinue={handleContinueFromData}
        onSkip={onSkip}
      >
        <OnboardingData selection={dataSelection} onSelect={setDataSelection} />
      </OnboardingLayout>
    );
  }

  const documentReady = generation.status === 'success';
  const dealLabel = dataSelection.kind === 'hubspot_deal' ? dataSelection.dealName : ONBOARDING_SAMPLE_COMPANY;
  const graphic = <OnboardingTemplateGraphic src={template.taggedGraphic} alt={template.label} />;

  if (step === ONBOARDING_STEPS.QUESTIONS) {
    return (
      <OnboardingLayout
        title={questionsAnswered ? t('Generating your first document') : t('While we wait, tell us about your work')}
        subtitle={questionsAnswered ? undefined : t('This will help us find ways to save you the most time, improve the product experience, and help you get the most out of Portant.')}
        step={ONBOARDING_STEPS.QUESTIONS}
        preview={graphic}
        previewOverlay={!toastHidden && generation.status !== 'skipped' && (
          <OnboardingGenerateToast
            title={`${template.label}_Template`}
            subtitle={dealLabel}
            status={generation.status === 'error' ? 'error' : documentReady ? 'complete' : 'generating'}
            onDismiss={() => setToastHidden(true)}
          />
        )}
        overlayKey={!toastHidden && generation.status !== 'skipped' ? "toast" : ""}
        footerNote={saveError
          ? <span className="text-red">{saveError}</span>
          : (generation.status === 'running' || documentReady) && (
            <span className="text-blue">
              {documentReady ? t('Your first document is ready!') : t('Waiting for your document to finish generating...')}
            </span>
          )}
        onBack={() => setStep(ONBOARDING_STEPS.DATA)}
        continueDisabled={saveError ? false : !questionsComplete || !(documentReady || generation.status === 'skipped') || saving}
        continueLabel={saveError ? t('Continue without saving') : saving ? t('Saving...') : t('Continue')}
        onContinue={saveError ? () => setStep(ONBOARDING_STEPS.REVEAL) : handleSaveQuestions}
        onSkip={questionsAnswered && !generatingSkipShown ? undefined : onSkip}
      >
        {generation.status === 'error' && (
          <div className="flex flex-col gap-2 border border-yellow rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-gray-600">{generation.error}</p>
            <div className="flex gap-2">
              <button type="button" className="btn btn-blue text-sm py-1 px-4" onClick={generation.retry}>{t('Retry')}</button>
              <button type="button" className="btn btn-gray text-sm py-1 px-4" onClick={generation.skip}>{t('Skip')}</button>
            </div>
          </div>
        )}
        {!questionsAnswered && <OnboardingQuestions answers={answers} onChange={setAnswers} />}
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      title={t("That's it! You've generated your first dynamic document")}
      subtitle={t('Your account setup is now complete.')}
      step={ONBOARDING_STEPS.REVEAL}
      preview={generation.previewUrl
        ? <OnboardingPdfFrame pdfUrl={generation.previewUrl} fileTitle={generation.documentName ?? `${template.label}_Template-${dealLabel.replace(/\s+/g, '_')}`} />
        : <OnboardingTemplateGraphic src={template.graphic} alt={template.label} />}
      previewKey={generation.previewUrl ? "pdf" : "graphic"}
      reveal
      footerNote={<span className="text-blue">{t('Time to personalize this workflow.')}</span>}
      onContinue={handleComplete}
      onSkip={onSkip}
    >
      <div className="flex flex-col gap-4 w-full text-left">
        <p className="font-gilroy font-semibold text-sm text-gray-400">
          {t('Here are some next steps to help get you off and running:')}
        </p>
        <ul className="text-sm font-semibold text-gray-500 list-disc pl-5 space-y-2">
          {dataSelection.kind === 'hubspot_deal' && <li>{t('Find this document any time in your Outputs page.')}</li>}
          <li>{t('Customize your Workflows to add emails and eSignatures.')}</li>
          <li>{t('Invite teammates to join your team and collaborate.')}</li>
        </ul>
        <p className="font-gilroy font-semibold text-sm text-gray-400">
          {t('You can now customize your templates, connect new data sources, request signatures and send documents!')}
        </p>
      </div>
    </OnboardingLayout>
  );
}

export default Onboarding;
